import { useEffect, useMemo, useState } from "react";
import { GridLayout, useContainerWidth, type Layout, type LayoutItem } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import {
  AD_LEVEL_LABELS,
  api, type AdLevel, type DashboardSpec, type MetricKey, type ProductRow, type QueueCard,
  type SeriesKey, type Widget, type WidgetSize,
} from "../api";
import { useAsync } from "../useAsync";
import { BarsSkeleton, ChartSkeleton, FunnelSkeleton } from "./Skeleton";
import type { CardAction } from "./Card";
import { ActionQueue } from "./ActionQueue";
import { FunnelStages } from "./FunnelStages";
import { PaybackChart } from "./PaybackChart";
import { TrendChart } from "./TrendChart";
import { channelColor, channelLabel, count, delta, money, ratio, toneColor, DASH, type Delta } from "../format";

/**
 * The widget vocabulary. Every widget renders from an endpoint the API already
 * serves — a dashboard is composition, never a new query. The same defs drive
 * the renderer, the editor's add-menu, and the default Overview spec.
 *
 * Layout is react-grid-layout (MIT): 12 columns, 32px rows, drag by the ⠿
 * handle, resize from the corner. Placement persists as each widget's
 * `layout` in the spec — same contract the API validates and agents write.
 */

export type QueuePlumbing = {
  cards: QueueCard[];
  errors: Record<string, string>;
  busyId: string | null;
  onAct: (id: string, action: CardAction) => void;
};

export const GRID_COLS = 12;
export const GRID_ROW = 32;
const GRID_MARGIN: readonly [number, number] = [14, 14];

/** Footprint for widgets that don't carry an explicit layout. */
const DEFAULT_WH: Record<Widget["type"], { w: number; h: number }> = {
  metric: { w: 2, h: 4 },
  timeseries: { w: 6, h: 8 },
  funnel: { w: 6, h: 11 },
  payback: { w: 12, h: 15 },
  queue: { w: 12, h: 10 },
  breakdown: { w: 6, h: 8 },
  ad_breakdown: { w: 6, h: 9 },
  top_social_posts: { w: 6, h: 9 },
  note: { w: 6, h: 4 },
};

const SIZE_W: Record<WidgetSize, number> = { xs: 2, sm: 3, md: 6, lg: 12 };

/**
 * Spec → grid items. Widgets with a saved layout keep it verbatim; the rest
 * shelf-pack left-to-right BELOW everything placed, so an agent- or
 * composer-built spec (no layouts) reads top-down in spec order, and a widget
 * added during editing lands at the bottom instead of shoving the board.
 */
export function specLayouts(widgets: Widget[]): LayoutItem[] {
  const bottom = widgets.reduce((m, w) => (w.layout ? Math.max(m, w.layout.y + w.layout.h) : m), 0);
  let cx = 0;
  let cy = bottom;
  let rowH = 0;
  return widgets.map((wg) => {
    if (wg.layout) return { i: wg.id, ...wg.layout, minW: 2, minH: 3 };
    const d = DEFAULT_WH[wg.type];
    const w = wg.size ? SIZE_W[wg.size] : d.w;
    if (cx + w > GRID_COLS) {
      cx = 0;
      cy += rowH;
      rowH = 0;
    }
    const item: LayoutItem = { i: wg.id, x: cx, y: cy, w, h: d.h, minW: 2, minH: 3 };
    cx += w;
    rowH = Math.max(rowH, d.h);
    return item;
  });
}

/** The built-in Overview — the pre-redesign product page, expressed as a spec.
 *  Not stored server-side; "Customise" copies it into a saved dashboard. */
export const OVERVIEW_SPEC: DashboardSpec = {
  widgets: [
    { id: "o1", type: "metric", metric: "spend_30d" },
    { id: "o2", type: "metric", metric: "revenue_30d" },
    { id: "o3", type: "metric", metric: "customers_30d" },
    { id: "o4", type: "metric", metric: "cac" },
    { id: "o5", type: "metric", metric: "ltv_cac" },
    { id: "o6", type: "metric", metric: "leads_30d" },
    { id: "o7", type: "queue" },
    { id: "o8", type: "payback" },
    { id: "o9", type: "funnel", days: 30 },
    { id: "o10", type: "breakdown", measure: "revenue_first", days: 30 },
  ],
};

// ------------------------------------------------------------------ metric

const METRIC_LABELS: Record<MetricKey, string> = {
  spend_30d: "Spend 30d", revenue_30d: "Revenue 30d", customers_30d: "New customers",
  cac: "CAC", ltv: "LTV", ltv_cac: "LTV:CAC", visits_30d: "Visits 30d",
  leads_30d: "Leads 30d", pending_cards: "Pending cards",
  search_clicks: "Search clicks 30d", search_impressions: "Search impressions 30d",
  ai_visibility: "AI visibility",
  social_followers: "Social followers", social_reach: "Social reach",
  social_engagement_rate: "Social engagement",
};

function metricView(key: MetricKey, p: ProductRow): { value: string; delta: Delta | null; note?: string } {
  const cur = p.currency;
  const prevCac = p.customers_prev_30d > 0 ? Math.round(p.spend_prev_30d_cents / p.customers_prev_30d) : null;
  switch (key) {
    case "spend_30d": return { value: money(p.spend_30d_cents, cur), delta: delta(p.spend_30d_cents, p.spend_prev_30d_cents, "neutral") };
    case "revenue_30d": return { value: money(p.revenue_30d_cents, cur), delta: delta(p.revenue_30d_cents, p.revenue_prev_30d_cents, "up") };
    case "customers_30d": return { value: count(p.customers_30d), delta: delta(p.customers_30d, p.customers_prev_30d, "up") };
    case "cac": return { value: money(p.cac_cents, cur), delta: delta(p.cac_cents, prevCac, "down") };
    case "ltv": return { value: money(p.ltv_cents, cur), delta: null, note: p.ltv_cents === null ? "no paying customers yet" : "per paying customer" };
    case "ltv_cac": return { value: ratio(p.ltv_cac), delta: null, note: p.ltv_cents !== null ? `LTV ${money(p.ltv_cents, cur)}` : "no paying customers yet" };
    case "visits_30d": return { value: count(p.visits_30d), delta: delta(p.visits_30d, p.visits_prev_30d, "up") };
    case "leads_30d": return { value: count(p.leads_30d), delta: delta(p.leads_30d, p.leads_prev_30d, "up") };
    case "pending_cards": return { value: count(p.pending_cards), delta: null, note: p.pending_cards > 0 ? "awaiting your decision" : "queue clear" };
    case "search_clicks":
    case "search_impressions":
    case "ai_visibility":
    case "social_followers":
    case "social_reach":
    case "social_engagement_rate":
      return { value: DASH, delta: null };
  }
}

const SERIES_META: Record<SeriesKey, { label: string; isMoney: boolean; pick: (r: { spend_cents: number; revenue_cents: number; visits: number; leads: number; customers: number; search_clicks: number; search_impressions: number; ai_visibility: number | null; social_impressions: number; social_engagement: number; social_followers: number }) => number }> = {
  spend: { label: "Spend", isMoney: true, pick: (r) => r.spend_cents },
  revenue: { label: "Revenue", isMoney: true, pick: (r) => r.revenue_cents },
  visits: { label: "Visits", isMoney: false, pick: (r) => r.visits },
  leads: { label: "Leads", isMoney: false, pick: (r) => r.leads },
  customers: { label: "New customers", isMoney: false, pick: (r) => r.customers },
  search_clicks: { label: "Search clicks", isMoney: false, pick: (r) => r.search_clicks },
  search_impressions: { label: "Search impressions", isMoney: false, pick: (r) => r.search_impressions },
  ai_visibility: { label: "AI visibility", isMoney: false, pick: (r) => r.ai_visibility ?? 0 },
  social_impressions: { label: "Social impressions", isMoney: false, pick: (r) => r.social_impressions },
  social_engagement: { label: "Social engagement", isMoney: false, pick: (r) => r.social_engagement },
  social_followers: { label: "Social followers", isMoney: false, pick: (r) => r.social_followers },
};

export function widgetTitle(w: Widget): string {
  if (w.title) return w.title;
  switch (w.type) {
    case "metric": return METRIC_LABELS[w.metric];
    case "timeseries": return `${SERIES_META[w.series].label} · daily, ${w.days}d`;
    case "funnel": return `Funnel · ${w.days}d`;
    case "payback": return "Payback curve";
    case "queue": return "Action queue";
    case "breakdown":
      return `${w.measure === "spend" ? "Spend" : w.measure === "revenue_first" ? "Revenue (first touch)" : "Revenue (last touch)"} by channel · ${w.days}d`;
    case "ad_breakdown":
      return `${w.measure === "roas" ? "ROAS" : w.measure === "spend" ? "Spend" : "Revenue"} by ${AD_LEVEL_LABELS[w.level].toLowerCase()} · ${w.days}d`;
    case "top_social_posts": return `Top social posts · ${w.days}d`;
    case "note": return "Note";
  }
}

// ------------------------------------------------------------------- board

export function WidgetBoard({
  spec,
  product,
  queue,
  editing = false,
  onChange,
}: {
  spec: DashboardSpec;
  product: ProductRow;
  queue: QueuePlumbing;
  editing?: boolean;
  onChange?: (spec: DashboardSpec) => void;
}) {
  const { width, mounted, containerRef } = useContainerWidth();
  const narrow = useNarrow();
  const layout = useMemo(() => specLayouts(spec.widgets), [spec.widgets]);

  const remove = (id: string) => onChange?.({ widgets: spec.widgets.filter((w) => w.id !== id) });

  // Drag/resize stops land here. Write positions back into the spec — the
  // grid never holds state the API wouldn't see. Compaction fires this on
  // mount too, so only editing sessions (and real changes) propagate.
  const applyLayout = (next: Layout) => {
    if (!editing || !onChange) return;
    const byId = new Map(next.map((l) => [l.i, l]));
    const widgets = spec.widgets.map((wg) => {
      const l = byId.get(wg.id);
      return l ? { ...wg, layout: { x: l.x, y: l.y, w: l.w, h: l.h } } : wg;
    });
    if (JSON.stringify(widgets) !== JSON.stringify(spec.widgets)) onChange({ widgets });
  };

  if (spec.widgets.length === 0) {
    return (
      <div className="wempty">
        This dashboard is empty{editing ? " — use “Add widget” above to build it." : "."}
      </div>
    );
  }

  // Phones get a single column in reading order; arranging is a desktop job.
  if (narrow) {
    const order = spec.widgets
      .map((wg, i) => ({ wg, l: layout[i] }))
      .sort((a, b) => a.l.y - b.l.y || a.l.x - b.l.x);
    return (
      <div className="wstack">
        {order.map(({ wg }) => (
          <WidgetCell key={wg.id} widget={wg} product={product} queue={queue}
            editing={false} onRemove={() => {}} />
        ))}
      </div>
    );
  }

  return (
    // RGL's hook types refs the React 19 way (nullable); React 18's DOM ref
    // prop wants the non-nullable flavour. Same object at runtime.
    <div ref={containerRef as React.RefObject<HTMLDivElement>}
         className={`wboard${editing ? " wboard-editing" : ""}`}>
      {mounted && (
        <GridLayout
          width={width}
          layout={layout}
          gridConfig={{ cols: GRID_COLS, rowHeight: GRID_ROW, margin: GRID_MARGIN, containerPadding: [0, 0] }}
          dragConfig={{ enabled: editing, handle: ".wdrag" }}
          resizeConfig={{ enabled: editing }}
          onLayoutChange={applyLayout}
        >
          {spec.widgets.map((wg) => (
            <div key={wg.id} className="wcell">
              <WidgetCell widget={wg} product={product} queue={queue} editing={editing}
                onRemove={() => remove(wg.id)} />
            </div>
          ))}
        </GridLayout>
      )}
    </div>
  );
}

function useNarrow(): boolean {
  const [narrow, setNarrow] = useState(() => matchMedia("(max-width: 860px)").matches);
  useEffect(() => {
    const mq = matchMedia("(max-width: 860px)");
    const on = () => setNarrow(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return narrow;
}

function WidgetCell({
  widget, product, queue, editing, onRemove,
}: {
  widget: Widget; product: ProductRow; queue: QueuePlumbing; editing: boolean;
  onRemove: () => void;
}) {
  // The queue brings its own header and rail chrome; everything else sits in a panel.
  const bare = widget.type === "queue";
  const body = <WidgetBody widget={widget} product={product} queue={queue} />;

  return (
    <div className="wcellin">
      {editing && (
        <div className="wtools">
          <span className="wdrag" title="Drag to move">
            <span aria-hidden="true" style={{ fontSize: 11 }}>⠿</span>
            <span className="wtools-name">{widgetTitle(widget)}</span>
          </span>
          <button type="button" className="wtool wtool-x" onClick={onRemove} title="Remove widget">×</button>
        </div>
      )}
      {bare ? (
        <div className="wbody">{body}</div>
      ) : (
        <div className="panel wpanel wbody">
          {widget.type !== "metric" && (
            <div className="eyebrow" style={{ marginBottom: 12, flex: "none" }}>{widgetTitle(widget)}</div>
          )}
          {body}
        </div>
      )}
    </div>
  );
}

function WidgetBody({ widget, product, queue }: { widget: Widget; product: ProductRow; queue: QueuePlumbing }) {
  switch (widget.type) {
    case "metric": return <MetricW k={widget.metric} custom={widget.title} p={product} />;
    case "timeseries": return <TimeseriesW slug={product.slug} series={widget.series} days={widget.days} currency={product.currency} />;
    case "funnel": return <FunnelW slug={product.slug} days={widget.days} />;
    case "payback": return <PaybackW slug={product.slug} currency={product.currency} />;
    case "queue": return <ActionQueue cards={queue.cards} errors={queue.errors} busyId={queue.busyId} onAct={queue.onAct} />;
    case "breakdown": return <BreakdownW slug={product.slug} measure={widget.measure} days={widget.days} currency={product.currency} />;
    case "ad_breakdown": return <AdBreakdownW slug={product.slug} level={widget.level} measure={widget.measure} days={widget.days} currency={product.currency} />;
    case "top_social_posts": return <TopSocialPostsW slug={product.slug} days={widget.days} />;
    case "note": return <div style={{ fontSize: 13, lineHeight: 1.65, color: "var(--text-2)", whiteSpace: "pre-wrap" }}>{widget.text}</div>;
  }
}

function MetricW({ k, custom, p }: { k: MetricKey; custom?: string; p: ProductRow }) {
  const demandKey = k === "search_clicks" || k === "search_impressions" || k === "ai_visibility"
    || k === "social_followers" || k === "social_reach" || k === "social_engagement_rate";
  const series = useAsync(
    () => demandKey ? api.timeseries(p.slug, 30) : Promise.resolve(null),
    [p.slug, k],
    { key: demandKey ? `timeseries:${p.slug}:30` : `metric:${k}` },
  );
  let m = metricView(k, p);
  if (demandKey && series.data) {
    if (k === "search_clicks") {
      const n = series.data.reduce((a, r) => a + (r.search_clicks ?? 0), 0);
      m = { value: count(n), delta: null, note: "last 30 days" };
    } else if (k === "search_impressions") {
      const n = series.data.reduce((a, r) => a + (r.search_impressions ?? 0), 0);
      m = { value: count(n), delta: null, note: "last 30 days" };
    } else if (k === "social_followers") {
      const last = [...series.data].reverse().find((r) => (r.social_followers ?? 0) > 0);
      m = { value: count(last?.social_followers ?? 0), delta: null, note: last ? "latest snapshot" : "connect social" };
    } else if (k === "social_reach") {
      const n = series.data.reduce((a, r) => a + (r.social_impressions ?? 0), 0);
      m = { value: count(n), delta: null, note: "impressions, last 30 days" };
    } else if (k === "social_engagement_rate") {
      const imp = series.data.reduce((a, r) => a + (r.social_impressions ?? 0), 0);
      const eng = series.data.reduce((a, r) => a + (r.social_engagement ?? 0), 0);
      m = {
        value: imp > 0 ? `${Math.round((eng / imp) * 1000) / 10}%` : DASH,
        delta: null,
        note: imp > 0 ? "engagement / impressions" : "connect social",
      };
    } else {
      const last = [...series.data].reverse().find((r) => r.ai_visibility != null);
      m = {
        value: last?.ai_visibility != null ? last.ai_visibility.toFixed(1) : DASH,
        delta: null,
        note: last ? "Opinly snapshot" : "connect Opinly",
      };
    }
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
      <div className="eyebrow">{custom ?? METRIC_LABELS[k]}</div>
      <div className="num" style={{ fontSize: 25, letterSpacing: "-0.03em", whiteSpace: "nowrap" }}>{m.value}</div>
      <div className="num" style={{ fontSize: 12, color: m.delta ? toneColor(m.delta.tone) : "var(--text-faint)" }}>
        {m.delta ? m.delta.label : (m.note ?? "")}
      </div>
    </div>
  );
}

function TimeseriesW({ slug, series, days, currency }: { slug: string; series: SeriesKey; days: number; currency: string }) {
  const data = useAsync(() => api.timeseries(slug, days), [slug, days], { key: `timeseries:${slug}:${days}` });
  if (data.error) return <WErr msg={data.error} />;
  if (data.loading || !data.data) return <WLoading kind="chart" />;
  const meta = SERIES_META[series];
  return (
    <TrendChart
      values={data.data.map(meta.pick)}
      days={data.data.map((r) => r.day)}
      isMoney={meta.isMoney}
      currency={currency}
    />
  );
}

function FunnelW({ slug, days }: { slug: string; days: number }) {
  const data = useAsync(() => api.funnel(slug, days), [slug, days], { key: `funnel:${slug}:${days}` });
  // The widget draws the product's own funnel definition, same as the Funnel page.
  const def = useAsync(() => api.funnelDef.get(slug), [slug], { key: `funnelDef:${slug}` });
  if (data.error) return <WErr msg={data.error} />;
  if (data.loading || def.loading) return <WLoading kind="funnel" />;
  const ladder = def.data?.steps.map((s) => ({ key: s.stage, label: s.label }));
  return <FunnelStages rows={data.data ?? []} channel="All" ladder={ladder} />;
}

function PaybackW({ slug, currency }: { slug: string; currency: string }) {
  const data = useAsync(() => api.payback(slug), [slug], { key: `payback:${slug}` });
  if (data.error) return <WErr msg={data.error} />;
  if (data.loading) return <WLoading kind="chart" />;
  return <PaybackChart data={data.data ?? { cohorts: [], cac: [] }} currency={currency} selected="All" />;
}

function BreakdownW({ slug, measure, days, currency }: {
  slug: string; measure: "spend" | "revenue_first" | "revenue_last"; days: number; currency: string;
}) {
  const data = useAsync(() => api.attribution(slug, days), [slug, days], { key: `attribution:${slug}:${days}` });
  if (data.error) return <WErr msg={data.error} />;
  if (data.loading || !data.data) return <WLoading kind="bars" />;

  const rows = data.data.channels
    .map((c) => ({
      channel: c.channel,
      value: measure === "spend" ? c.spend_cents : measure === "revenue_first" ? c.revenue_first_cents : c.revenue_last_cents,
    }))
    .filter((r) => r.value !== 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, 8);
  const max = Math.max(...rows.map((r) => Math.abs(r.value)), 1);

  if (rows.length === 0) return <WErr msg="Nothing to break down in this window yet." soft />;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
      {rows.map((r) => (
        <div key={r.channel} style={{ display: "grid", gridTemplateColumns: "118px minmax(0,1fr) 76px", gap: 12, alignItems: "center" }}>
          <span style={{ fontSize: 12.5, color: r.channel === "unattributed" ? "var(--muted)" : "var(--text-2)",
                         overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {channelLabel(r.channel)}
          </span>
          <div style={{ height: 9, background: "var(--surface-inset)", borderRadius: 3, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${(Math.abs(r.value) / max) * 100}%`, borderRadius: 3,
                          background: r.channel === "unattributed" ? "var(--muted)" : channelColor(r.channel) }} />
          </div>
          <span className="num" style={{ fontSize: 12.5, textAlign: "right" }}>{money(r.value, currency)}</span>
        </div>
      ))}
    </div>
  );
}

/** The same read as the Attribution → Ads tab, one grain at a time. Sentinel
 *  rows are kept in the ROAS view (a keyword table that quietly drops the
 *  unplaceable money flatters itself) but dropped from the money bars, where
 *  they would just be a bar labelled "unknown". */
function AdBreakdownW({ slug, level, measure, days, currency }: {
  slug: string; level: AdLevel; measure: "roas" | "spend" | "revenue_first"; days: number; currency: string;
}) {
  const data = useAsync(() => api.attributionAds(slug, days, level, 12), [slug, days, level], { key: `ads:${slug}:${days}:${level}` });
  if (data.error) return <WErr msg={data.error} />;
  if (data.loading || !data.data) return <WLoading kind="bars" />;

  const named = data.data.rows.filter((r) => r.key !== "unattributed" && r.key !== "no_dimension");

  if (measure === "roas") {
    const rows = named.filter((r) => r.spend_cents > 0).sort((a, b) => (b.roas ?? 0) - (a.roas ?? 0)).slice(0, 8);
    if (rows.length === 0) return <WErr msg={`No ${AD_LEVEL_LABELS[level].toLowerCase()} spend in this window yet.`} soft />;
    const max = Math.max(...rows.map((r) => r.roas ?? 0), 1);
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
        {rows.map((r) => (
          <div key={r.key} style={{ display: "grid", gridTemplateColumns: "118px minmax(0,1fr) 76px", gap: 12, alignItems: "center" }}>
            <span title={r.label ?? r.key} style={{ fontSize: 12.5, color: "var(--text-2)",
                           overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {r.label || `#${r.key}`}
            </span>
            <div style={{ height: 9, background: "var(--surface-inset)", borderRadius: 3, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${((r.roas ?? 0) / max) * 100}%`, borderRadius: 3,
                            background: (r.roas ?? 0) >= 1 ? "var(--pos)" : "var(--neg)" }} />
            </div>
            <span className="num" style={{ fontSize: 12.5, textAlign: "right", color: (r.roas ?? 0) >= 1 ? "var(--pos)" : "var(--neg)" }}>
              {r.roas === null ? DASH : `${Number(r.roas).toFixed(2)}×`}
            </span>
          </div>
        ))}
      </div>
    );
  }

  const rows = named
    .map((r) => ({ key: r.key, label: r.label || `#${r.key}`, value: measure === "spend" ? r.spend_cents : r.revenue_first_cents }))
    .filter((r) => r.value !== 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, 8);
  if (rows.length === 0) return <WErr msg="Nothing to break down in this window yet." soft />;
  const max = Math.max(...rows.map((r) => Math.abs(r.value)), 1);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
      {rows.map((r) => (
        <div key={r.key} style={{ display: "grid", gridTemplateColumns: "118px minmax(0,1fr) 76px", gap: 12, alignItems: "center" }}>
          <span title={r.label} style={{ fontSize: 12.5, color: "var(--text-2)",
                         overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {r.label}
          </span>
          <div style={{ height: 9, background: "var(--surface-inset)", borderRadius: 3, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${(Math.abs(r.value) / max) * 100}%`, borderRadius: 3,
                          background: "var(--keeper)" }} />
          </div>
          <span className="num" style={{ fontSize: 12.5, textAlign: "right" }}>{money(r.value, currency)}</span>
        </div>
      ))}
    </div>
  );
}

function TopSocialPostsW({ slug, days }: { slug: string; days: number }) {
  const data = useAsync(() => api.social(slug, days), [slug, days], { key: `social:${slug}:${days}` });
  if (data.error) return <WErr msg={data.error} />;
  if (data.loading || !data.data) return <WLoading kind="bars" />;
  const posts = data.data.posts.slice(0, 6);
  if (posts.length === 0) return <WErr msg="Connect Meta Social or X to see posts." soft />;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {posts.map((p) => (
        <div key={`${p.source}:${p.post_id}`} style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 56px 52px", gap: 10, alignItems: "baseline" }}>
          <span title={p.content_text ?? p.post_id} style={{ fontSize: 12.5, color: "var(--text-2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {p.post_url
              ? <a href={p.post_url} target="_blank" rel="noreferrer">{(p.content_text ?? p.post_id).slice(0, 72)}</a>
              : (p.content_text ?? p.post_id).slice(0, 72)}
          </span>
          <span className="num" style={{ fontSize: 12, color: "var(--text-3)", textAlign: "right" }}>
            {p.source === "x_social" ? "X" : "Meta"}
          </span>
          <span className="num" style={{ fontSize: 12.5, textAlign: "right" }}>
            {p.engagement_rate != null ? `${Math.round(p.engagement_rate * 1000) / 10}%` : DASH}
          </span>
        </div>
      ))}
    </div>
  );
}

function WLoading({ kind }: { kind: "chart" | "bars" | "funnel" }) {
  if (kind === "bars") return <BarsSkeleton />;
  if (kind === "funnel") return <FunnelSkeleton />;
  return <ChartSkeleton />;
}

function WErr({ msg, soft }: { msg: string; soft?: boolean }) {
  return (
    <div style={{ padding: "22px 0", fontSize: 12.5, color: soft ? "var(--text-faint)" : "var(--neg)", textAlign: "center" }}>
      {msg || DASH}
    </div>
  );
}
